import { API_BASE } from "../services/apiConfig.js";
import AccountSync from "../services/AccountSync.js";
import { escapeHtml, renderProfileHeader, renderProfileStats } from "../utils/PlayerProfile.js";

const GAME_LABELS = {
    "flagnerd": "Flag Nerd Classic",
    "flagnerdhard": "Flag Nerd Expert",
    "flagnerd.speedrun": "Flag Nerd Speed Run",
    "capitalnerd": "Capital Nerd Classic",
    "capitalnerdhard": "Capital Nerd Expert",
    "capitalnerd.speedrun": "Capital Nerd Speed Run",
    "capitalnerdreverse": "Capital Nerd Inverse",
    "mapnerdclassic": "Map Nerd Classic",
    "mapnerdhard": "Map Nerd Expert",
    "countrynerd": "Country Nerd",
    "countrynerdhard": "Country Nerd Hard",
    "dailychallenge": "Défi du jour",
    "dailychallengehard": "Défi du jour Expert",
    "usstatesclassic": "US States Classic",
    "usstateshard": "US States Expert",
    "usstateslist": "US States List",
    "friendchallenge": "Défi un ami",
    "bordernerd": "Border Nerd",
    "shapenerd": "Shape Nerd",
    "shapenerdhard": "Shape Nerd Hard",
};

/** Modes du Lab (voir `server/src/routes/lab.js`). */
const LAB_LABELS = {
    friendchallenge: "Défi un ami",
    bordernerd: "Border Nerd",
    shapenerd: "Shape Nerd",
    shapenerdhard: "Shape Nerd Hard",
};

async function api(method, path, body) {
    let res;
    try {
        res = await fetch(`${API_BASE}${path}`, {
            method,
            credentials: "include",
            headers: body ? { "Content-Type": "application/json" } : undefined,
            body: body ? JSON.stringify(body) : undefined,
        });
    } catch {
        return { ok: false, status: 0, data: null };
    }
    let data = null;
    try { data = await res.json(); } catch { /* vide */ }
    return { ok: res.ok, status: res.status, data };
}

function fmtDate(ts) {
    if (!ts) return "—";
    return new Date(ts).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const WEEK_LABEL_FORMAT = new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "long" });

function fmtWeekLabel(isoDate) {
    const start = new Date(`${isoDate}T00:00:00Z`);
    const end = new Date(start.getTime() + 7 * 86400000);
    return `semaine du ${WEEK_LABEL_FORMAT.format(start)} au ${WEEK_LABEL_FORMAT.format(end)}`;
}

function fmtDateTime(ts) {
    if (!ts) return "—";
    return new Date(ts).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/**
 * Rétention des appareils (avec ou sans compte), par cohorte de semaine
 * d'arrivée. Un pourcentage vide veut dire « pas encore mesurable » : la cohorte
 * est trop récente pour qu'une visite à J+7 ou J+30 ait pu avoir lieu.
 */
function renderDeviceRetention(devices) {
    const cell = (v) => (v == null ? "·" : `${v}&nbsp;%`);
    const since = devices.measuredSince
        ? new Date(devices.measuredSince).toLocaleDateString("fr-FR")
        : null;
    const rows = [...devices.cohorts].reverse().map(c => `
        <tr>
            <td>${fmtWeekLabel(c.weekStart)}</td>
            <td>${c.count}</td>
            <td>${cell(c.returned)}</td>
            <td>${cell(c.d7)}</td>
            <td>${cell(c.d30)}</td>
        </tr>
    `).join("");
    return `
        <h3 class="admin-section-title admin-section-title--spaced">Rétention des joueurs (appareils)</h3>
        <div class="admin-stat-cards">
            <div class="admin-stat-card">
                <div class="admin-stat-label">Appareils actifs sur 7 jours</div>
                <div class="admin-stat-value">${devices.active.d7}</div>
            </div>
            <div class="admin-stat-card">
                <div class="admin-stat-label">Appareils actifs sur 30 jours</div>
                <div class="admin-stat-value">${devices.active.d30}</div>
            </div>
        </div>
        ${devices.cohorts.length ? `
        <table class="admin-retention-table">
            <thead><tr><th>Arrivés la semaine du</th><th>Joueurs</th><th>Revenus</th><th>Encore là à J+7</th><th>Encore là à J+30</th></tr></thead>
            <tbody>${rows}</tbody>
        </table>` : `<p class="admin-empty">Pas encore de cohorte mesurée.</p>`}
        ${since ? `<p class="admin-note">Mesure en place depuis le ${since} : les joueurs arrivés avant ne sont pas comptés dans les cohortes.</p>` : ""}
    `;
}

export default class Admin {
    constructor() {
        this.tbody = document.querySelector(".admin-users-body");
        this.profilePopup = document.querySelector(".admin-profile-popup");
        this.profileBody = document.querySelector(".admin-profile-popup .player-profile-body");
        this.search = document.querySelector(".admin-search");
        this.filterSuspect = document.querySelector(".admin-filter-suspect-checkbox");
        this.playerStats = document.querySelector(".admin-player-stats");
        this.tabs = document.querySelectorAll(".admin-tab");
        this.panels = document.querySelectorAll(".admin-tab-panel");
        this.modesBody = document.querySelector(".admin-modes-body");
        this.modesRecent = document.querySelector(".admin-modes-recent-checkbox");
        this.modesScope = document.querySelector(".admin-modes-scope");
        this.activityBody = document.querySelector(".admin-activity-body");
        this.labBody = document.querySelector(".admin-lab-body");
        this.mailingBody = document.querySelector(".admin-mailing-body");
        if (!this.tbody) return;
        this.users = [];
        this.selectedId = null;
        this.sortKey = null;
        this.sortDir = 1;
        this.lang = window.geoNerdApp.languageManager;
        this.initProfilePopup();
        this.init();
    }

    /** Même mécanique d'ouverture/fermeture que la popup publique du classement (`Leaderboard.js`). */
    initProfilePopup() {
        if (!this.profilePopup) return;
        this.profilePopup.querySelector(".player-profile-close")
            .addEventListener("click", () => this.closeProfilePopup());
        this.profilePopup.addEventListener("click", (e) => {
            if (e.target === this.profilePopup) this.closeProfilePopup();
        });
        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && this.profilePopup.classList.contains("show")) this.closeProfilePopup();
        });
    }

    closeProfilePopup() {
        this.profilePopup.classList.remove("show");
        this.selectedId = null;
        this.renderTable();
    }

    async init() {
        // Attendre que la session soit établie avant de vérifier les droits.
        if (window.geoNerdApp?.accountReady) await window.geoNerdApp.accountReady;
        if (!AccountSync.user?.is_admin) {
            window.location.href = window.geoNerdApp.languageManager.currentLang === "en" ? "/en/" : "/";
            return;
        }
        document.body.classList.add("is-admin");
        await this.loadUsers();
        this.loadPlayerStats();
        this.search?.addEventListener("input", () => this.renderTable());
        this.filterSuspect?.addEventListener("change", () => this.renderTable());
        this.initTabs();
        this.initSortHeaders();
    }

    initSortHeaders() {
        document.querySelectorAll(".admin-table thead th[data-sort]").forEach(th => {
            th.addEventListener("click", () => {
                if (this.sortKey === th.dataset.sort) {
                    this.sortDir *= -1;
                } else {
                    this.sortKey = th.dataset.sort;
                    this.sortDir = 1;
                }
                this.updateSortIndicators();
                this.renderTable();
            });
        });
    }

    updateSortIndicators() {
        document.querySelectorAll(".admin-table thead th[data-sort]").forEach(th => {
            const arrow = th.querySelector(".admin-sort-arrow");
            const active = th.dataset.sort === this.sortKey;
            th.classList.toggle("is-sorted", active);
            if (arrow) arrow.textContent = active ? (this.sortDir === 1 ? " ▲" : " ▼") : "";
        });
    }

    initTabs() {
        this.tabs.forEach(tab => {
            tab.addEventListener("click", () => {
                this.tabs.forEach(t => t.classList.remove("is-active"));
                this.panels.forEach(p => p.classList.remove("is-active"));
                tab.classList.add("is-active");
                document.querySelector(`.admin-tab-panel[data-panel="${tab.dataset.tab}"]`)?.classList.add("is-active");
                if (tab.dataset.tab === "modes" && !this.modesLoaded) this.loadGameModes();
                if (tab.dataset.tab === "activity" && !this.activityLoaded) this.loadActivity();
                if (tab.dataset.tab === "lab" && !this.labLoaded) this.loadLab();
                if (tab.dataset.tab === "mailing" && !this.mailingLoaded) this.loadMailing();
            });
        });
    }

    /**
     * Le Lab : ce qui est joué (parties et joueurs distincts, avec ou sans
     * compte) et ce qui plaît (note moyenne, répartition, derniers commentaires).
     * Trié par parties des 7 derniers jours.
     */
    async loadLab() {
        if (!this.labBody) return;
        this.labLoaded = true;
        this.labBody.innerHTML = `<p class="admin-loading">Chargement…</p>`;
        const { ok, data } = await api("GET", "/geoadmin/lab");
        if (!ok) {
            this.labBody.innerHTML = `<p class="admin-error">Erreur de chargement.</p>`;
            this.labLoaded = false;
            return;
        }
        const modes = [...data.modes].sort((a, b) => b.plays7 - a.plays7 || b.plays - a.plays);
        const maxPlays = Math.max(1, ...modes.map(m => m.plays7));
        const stars = (avg) => avg == null ? "·" : `${"★".repeat(Math.round(avg))}${"☆".repeat(5 - Math.round(avg))} ${String(avg).replace(".", ",")}`;
        const rows = modes.map(m => `
            <div class="admin-mode-row admin-lab-row">
                <div class="admin-mode-label">${escHtml(LAB_LABELS[m.mode] || m.mode)}</div>
                <div class="admin-mode-bar-wrap">
                    <div class="admin-mode-bar" style="width:${Math.round(m.plays7 / maxPlays * 100)}%"></div>
                </div>
                <div class="admin-lab-figures">
                    <span><strong>${m.plays7}</strong> parties / 7 j · ${m.players7} joueurs</span>
                    <span>${m.plays} parties au total · ${m.players} joueurs</span>
                    <span class="admin-lab-stars" title="${m.distribution.map((n, i) => `${i + 1}★ : ${n}`).join(" · ")}">${stars(m.average)} <small>(${m.ratings} avis)</small></span>
                </div>
            </div>
        `).join("");
        const comments = modes.flatMap(m => m.comments.map(c => ({ ...c, mode: m.mode })))
            .sort((a, b) => b.at - a.at)
            .map(c => `
                <li class="admin-lab-comment">
                    <span class="admin-lab-comment-meta">${escHtml(LAB_LABELS[c.mode] || c.mode)} · ${"★".repeat(c.rating)} · ${new Date(c.at).toLocaleDateString("fr-FR")}${c.lang ? ` · ${c.lang}` : ""}</span>
                    <span>${escHtml(c.comment)}</span>
                </li>`).join("");
        this.labBody.innerHTML = `
            <h3 class="admin-section-title">Les plus joués cette semaine</h3>
            <div class="admin-modes-body">${rows}</div>
            <h3 class="admin-section-title admin-section-title--spaced">Derniers commentaires</h3>
            ${comments ? `<ul class="admin-lab-comments">${comments}</ul>` : `<p class="admin-empty">Aucun commentaire pour l'instant.</p>`}
        `;
    }

    /**
     * Mailing des nouveautés : aperçu FR et EN tel qu'il partira, destinataires
     * par langue, test envoyé à soi seul, puis envoi à tous après avoir retapé
     * le nombre de destinataires. Les e-mails partent ensuite par lots côté
     * serveur ; l'avancement se rafraîchit tant qu'il en reste en file.
     */
    async loadMailing() {
        if (!this.mailingBody) return;
        this.mailingLoaded = true;
        clearTimeout(this.mailingPoll);
        if (!this.mailingData) this.mailingBody.innerHTML = `<p class="admin-loading">Chargement…</p>`;
        const { ok, data } = await api("GET", "/geoadmin/mailing");
        if (!ok) {
            this.mailingBody.innerHTML = `<p class="admin-error">Erreur de chargement.</p>`;
            this.mailingLoaded = false;
            return;
        }
        this.mailingData = data;
        this.renderMailing(data);
        if (data.progress?.pending > 0) this.mailingPoll = setTimeout(() => this.loadMailing(), 15000);
    }

    renderMailing(data) {
        const { recipients: r, progress: p, previews } = data;
        const card = (label, value) => `
            <div class="admin-stat-card">
                <div class="admin-stat-label">${label}</div>
                <div class="admin-stat-value">${value}</div>
            </div>`;
        const s = data.schedule || {};
        const left = p ? p.pending : r.total;
        const days = s.budget ? Math.ceil(left / s.budget) : null;
        const pace = `chaque jour à partir de ${s.hour ?? 0} h (${escHtml(s.timezone || "UTC")}), par lots de ${data.batchSize} par minute${s.budget ? `, ${s.budget} au plus par jour (plafond et quota Resend, rappels déduits)` : ""}, les comptes les plus actifs d'abord${days ? `. Environ ${days} jour${days > 1 ? "s" : ""} pour ${left} destinataire${left > 1 ? "s" : ""}` : ""}${s.budget ? `. Aujourd'hui : ${s.sentToday} envoyé${s.sentToday > 1 ? "s" : ""}, ${s.remaining} encore possible${s.remaining > 1 ? "s" : ""}` : ""}`;
        const preview = (lang, label) => `
            <figure class="admin-mailing-preview">
                <figcaption><strong>${label}</strong> · ${escHtml(previews[lang].subject)}</figcaption>
                <iframe class="admin-mailing-frame" data-lang="${lang}" title="Aperçu ${label}" sandbox="allow-popups allow-popups-to-escape-sandbox"></iframe>
            </figure>`;

        const progress = p ? `
            <h3 class="admin-section-title admin-section-title--spaced">Envoi lancé le ${fmtDateTime(p.startedAt)}</h3>
            <div class="admin-mode-bar-wrap admin-mailing-bar"><div class="admin-mode-bar" style="width:${p.total ? Math.round(p.sent / p.total * 100) : 0}%"></div></div>
            <div class="admin-stat-cards">
                ${card("Envoyés", `${p.sent} <small>/ ${p.total}</small>`)}
                ${card("En file", p.pending)}
                ${card("Échecs", p.failed)}
                ${card("Sautés", `${p.skipped} <small>désinscrits entre-temps</small>`)}
            </div>
            <p class="admin-note">${p.byLang.fr ?? 0} en français, ${p.byLang.en ?? 0} en anglais (langue de repli : ${p.fallbackLang === "fr" ? "français" : "anglais"}). Envoi ${pace}${p.pending ? " ; la page se met à jour toute seule." : "."}</p>` : `
            <form class="admin-mailing-send">
                <h3 class="admin-section-title admin-section-title--spaced">Envoyer à tous</h3>
                <fieldset class="admin-mailing-fallback">
                    <legend>Langue des ${r.unknown} comptes sans indice</legend>
                    <label><input type="radio" name="fallbackLang" value="en" checked> Anglais</label>
                    <label><input type="radio" name="fallbackLang" value="fr"> Français</label>
                </fieldset>
                <label class="admin-mailing-confirm">
                    Pour confirmer, tape le nombre de destinataires (${r.total})
                    <input type="text" inputmode="numeric" autocomplete="off" class="admin-mailing-confirm-input">
                </label>
                <button type="submit" class="btn btn-red admin-mailing-send-btn" disabled>Envoyer à ${r.total} comptes</button>
                <p class="admin-note">Une campagne ne part qu'une fois : personne ne la reçoit deux fois. Envoi ${pace}.</p>
            </form>`;

        this.mailingBody.innerHTML = `
            <h3 class="admin-section-title">Nouveautés <small class="admin-mailing-campaign">campagne ${escHtml(data.campaign)}</small></h3>
            <div class="admin-stat-cards">
                ${card("Destinataires", r.total)}
                ${card("En français", r.byLang.fr)}
                ${card("En anglais", r.byLang.en)}
                ${card("Sans indice de langue", r.unknown)}
            </div>
            <p class="admin-note">Langue tirée du compte (${r.bySource.account}), des notifications (${r.bySource.device}), de la progression (${r.bySource.progress}) ou d'une adresse en .fr (${r.bySource.email}). Les comptes qui ont coupé les nouveautés ne sont pas comptés.</p>
            <div class="admin-mailing-previews">
                ${preview("fr", "FR")}
                ${preview("en", "EN")}
            </div>
            <div class="admin-mailing-test">
                <button type="button" class="btn btn-secondary admin-mailing-test-btn">M'envoyer un test (FR et EN)</button>
                <p class="admin-mailing-message" role="status"></p>
            </div>
            ${progress}`;

        if (this.mailingNotice) {
            const message = this.mailingBody.querySelector(".admin-mailing-message");
            message.textContent = this.mailingNotice;
            message.classList.add("is-error");
            this.mailingNotice = null;
        }

        // srcdoc posé en propriété : pas d'échappement d'attribut à gérer.
        this.mailingBody.querySelectorAll(".admin-mailing-frame").forEach(frame => {
            frame.srcdoc = previews[frame.dataset.lang].html;
        });
        this.mailingBody.querySelector(".admin-mailing-test-btn").addEventListener("click", (e) => this.sendMailingTest(e.currentTarget));

        const form = this.mailingBody.querySelector(".admin-mailing-send");
        if (!form) return;
        const input = form.querySelector(".admin-mailing-confirm-input");
        const button = form.querySelector(".admin-mailing-send-btn");
        input.addEventListener("input", () => {
            button.disabled = input.value.trim() !== String(r.total) || r.total === 0;
        });
        form.addEventListener("submit", (e) => this.sendMailing(e, form));
    }

    async sendMailingTest(button) {
        const message = this.mailingBody.querySelector(".admin-mailing-message");
        button.disabled = true;
        const { ok, data } = await api("POST", "/geoadmin/mailing/test", {});
        button.disabled = false;
        message.textContent = ok
            ? `Test envoyé à ${data.to} (FR et EN).`
            : "Le test n'est pas parti. Réessaie dans un moment.";
        message.classList.toggle("is-error", !ok);
    }

    async sendMailing(e, form) {
        e.preventDefault();
        const button = form.querySelector(".admin-mailing-send-btn");
        button.disabled = true;
        const fallbackLang = form.querySelector('input[name="fallbackLang"]:checked')?.value || "en";
        const confirm = parseInt(form.querySelector(".admin-mailing-confirm-input").value, 10);
        const { ok, status, data } = await api("POST", "/geoadmin/mailing/send", { confirm, fallbackLang });
        if (!ok) {
            // Un compte s'est inscrit ou désinscrit entre-temps : on recharge les chiffres.
            this.mailingNotice = status === 400 && data?.error === "confirm_mismatch"
                ? `Le nombre de destinataires a changé (${data.total}). Vérifie et confirme de nouveau.`
                : status === 409 ? "Cette campagne est déjà lancée." : "L'envoi n'a pas pu être lancé. Réessaie dans un moment.";
        }
        await this.loadMailing();
    }

    async loadGameModes() {
        if (!this.modesBody) return;
        this.modesLoaded = true;
        if (this.modesRecent && !this.modesRecent.dataset.bound) {
            this.modesRecent.dataset.bound = "1";
            this.modesRecent.addEventListener("change", () => this.loadGameModes());
        }
        // Les deux vues ne mesurent pas la même population : on le dit.
        const recent = !!this.modesRecent?.checked;
        this.modesBody.innerHTML = `<p class="admin-loading">Chargement…</p>`;
        const { ok, data } = await api("GET", `/geoadmin/stats/game-modes${recent ? "?period=7d" : ""}`);
        if (!ok) {
            this.modesBody.innerHTML = `<p class="admin-error">Erreur de chargement.</p>`;
            this.modesLoaded = false;
            return;
        }
        if (recent !== !!this.modesRecent?.checked) return; // case rebasculée pendant le chargement
        const { modes } = data;
        if (this.modesScope) {
            this.modesScope.textContent = recent
                ? `Parties terminées depuis le ${new Date(`${data.since}T00:00:00Z`).toLocaleDateString("fr-FR", { timeZone: "UTC" })}, joueurs avec ou sans compte.`
                : "Depuis le début, comptes uniquement (sauvegardes synchronisées).";
        }
        const max = Math.max(1, ...modes.map(m => m.gamesPlayed));
        this.modesBody.innerHTML = modes.map(m => `
            <div class="admin-mode-row">
                <div class="admin-mode-label">${GAME_LABELS[m.gameId] ?? m.gameId}</div>
                <div class="admin-mode-bar-wrap">
                    <div class="admin-mode-bar" style="width:${Math.round(m.gamesPlayed / max * 100)}%"></div>
                </div>
                <div class="admin-mode-count">
                    <div class="admin-mode-count-value">${m.gamesPlayed}</div>
                    ${m.avgScore != null ? `<div class="admin-mode-avg">moy. ${m.avgScore}</div>` : ""}
                    ${m.players != null ? `<div class="admin-mode-avg">${m.players} joueur${m.players > 1 ? "s" : ""}</div>` : ""}
                </div>
            </div>
        `).join("");
    }

    async loadActivity() {
        if (!this.activityBody) return;
        this.activityLoaded = true;
        this.activityBody.innerHTML = `<p class="admin-loading">Chargement…</p>`;
        const { ok, data } = await api("GET", "/geoadmin/stats/overview");
        if (!ok) {
            this.activityBody.innerHTML = `<p class="admin-error">Erreur de chargement.</p>`;
            this.activityLoaded = false;
            return;
        }
        const { activeUsers, dailyStreaks, signupsByWeek, devices } = data;

        const streakBlock = (label, s) => `
            <div class="admin-streak-card">
                <div class="admin-stat-label">${label}</div>
                <div class="admin-stat-value">${s.activeCount} <small>série(s) en cours</small></div>
                <div class="admin-streak-longest">${s.longest
                    ? `record : <strong>${s.longest.value}</strong> jours (${escHtml(s.longest.username)})`
                    : "aucune série enregistrée"}</div>
            </div>
        `;

        const maxSignups = Math.max(1, ...signupsByWeek.map(w => w.count));
        const weeksHtml = [...signupsByWeek].reverse().map(w => `
            <div class="admin-mode-row">
                <div class="admin-mode-label">${fmtWeekLabel(w.weekStart)}</div>
                <div class="admin-mode-bar-wrap">
                    <div class="admin-mode-bar" style="width:${Math.round(w.count / maxSignups * 100)}%"></div>
                </div>
                <div class="admin-mode-count">${w.count}</div>
            </div>
        `).join("");

        this.activityBody.innerHTML = `
            <div class="admin-stat-cards">
                <div class="admin-stat-card">
                    <div class="admin-stat-label">Actifs sur 7 jours</div>
                    <div class="admin-stat-value">${activeUsers.d7}</div>
                </div>
                <div class="admin-stat-card">
                    <div class="admin-stat-label">Actifs sur 30 jours</div>
                    <div class="admin-stat-value">${activeUsers.d30}</div>
                </div>
                ${streakBlock("Défi quotidien", dailyStreaks.classic)}
                ${streakBlock("Défi quotidien Expert", dailyStreaks.hard)}
            </div>
            <h3 class="admin-section-title">Inscriptions par semaine</h3>
            <div class="admin-modes-body">${weeksHtml}</div>
            ${devices ? renderDeviceRetention(devices) : ""}
        `;
    }

    async loadPlayerStats() {
        if (!this.playerStats) return;
        const { ok, data } = await api("GET", "/geoadmin/players/count");
        if (!ok) return;
        this.playerStats.innerHTML = `
            <span class="admin-stat"><strong>${data.anonymous}</strong> joueurs anonymes</span>
            <span class="admin-stat"><strong>${data.registered}</strong> personnes connectées</span>
        `;
    }

    async loadUsers() {
        const { ok, data } = await api("GET", "/geoadmin/users");
        if (!ok) {
            this.tbody.innerHTML = `<tr><td colspan="9" class="admin-error">Erreur de chargement (${data?.error ?? "inconnu"})</td></tr>`;
            return;
        }
        this.users = data;
        this.renderTable();
    }

    renderTable() {
        const q = (this.search?.value ?? "").toLowerCase().trim();
        let filtered = q
            ? this.users.filter(u => u.email.toLowerCase().includes(q) || (u.username ?? "").toLowerCase().includes(q))
            : this.users;
        if (this.filterSuspect?.checked) filtered = filtered.filter(u => u.flagged_suspect);

        if (this.sortKey) {
            const key = this.sortKey, dir = this.sortDir;
            filtered = [...filtered].sort((a, b) => {
                const va = a[key] ?? 0, vb = b[key] ?? 0;
                if (va < vb) return -dir;
                if (va > vb) return dir;
                return 0;
            });
        }

        if (!filtered.length) {
            this.tbody.innerHTML = `<tr><td colspan="7" class="admin-empty">Aucun utilisateur trouvé.</td></tr>`;
            return;
        }

        this.tbody.innerHTML = filtered.map(u => `
            <tr class="admin-row${this.selectedId === u.id ? " is-selected" : ""}" data-id="${u.id}">
                <td class="admin-id">${u.id}</td>
                <td class="admin-email">${escHtml(u.email)}</td>
                <td class="admin-username">${u.username ? escHtml(u.username) : '<span class="admin-none">—</span>'}</td>
                <td class="admin-badge-cell">
                    ${u.is_admin ? '<span class="admin-badge admin-badge--admin">admin</span>' : ''}
                    ${u.flagged_suspect ? '<span class="admin-badge admin-badge--suspect">⚠️ suspect</span>' : ''}
                    ${u.hidden_from_leaderboard ? '<span class="admin-badge admin-badge--hidden">🙈 masqué</span>' : ''}
                </td>
                <td class="admin-date">${fmtDate(u.created_at)}</td>
                <td class="admin-date">${fmtDateTime(u.last_active)}</td>
                <td class="admin-num">${u.totalGamesPlayed}</td>
            </tr>
        `).join("");

        this.tbody.querySelectorAll(".admin-row").forEach(row =>
            row.addEventListener("click", () => this.showStats(Number(row.dataset.id)))
        );
    }

    /** Country Nerd n'a pas de `best` (progression par lettre) : forme `{letters, found}` plutôt que `{best, gamesPlayed, cumulativeScore}`. */
    buildGamesMap(stats) {
        const games = {};
        for (const [gameId, s] of Object.entries(stats)) {
            if (gameId === "countrynerd" || gameId === "countrynerdhard") {
                games[gameId] = { score: s.letters, found: s.found };
                continue;
            }
            if (s.best === undefined) continue;
            const avgScore = s.gamesPlayed >= 2 && s.cumulativeScore != null
                ? Math.round(s.cumulativeScore / s.gamesPlayed * 10) / 10
                : null;
            games[gameId] = { score: Number(s.best), gamesPlayed: s.gamesPlayed, avgScore };
        }
        return games;
    }

    /** Défi quotidien : série en cours ET record, une info que la popup publique n'a pas (elle n'affiche que la série en cours d'un mode joué aujourd'hui). */
    renderStreakCard(label, streak) {
        if (!streak) return "";
        return `<div class="admin-profile-stat">
            <span class="admin-profile-stat-label">${label}</span>
            <span class="admin-profile-stat-value">${streak.current ?? 0} <small>en cours</small> · ${streak.longest ?? 0} <small>record</small></span>
        </div>`;
    }

    async showStats(id) {
        this.selectedId = id;
        this.renderTable();
        const row = this.users.find(u => u.id === id);
        this.profilePopup.classList.add("show");
        this.profileBody.innerHTML = `<p class="player-profile-loading">Chargement…</p>`;
        const { ok, data } = await api("GET", `/geoadmin/users/${id}/stats`);
        if (!ok) {
            this.profileBody.innerHTML = `<p class="player-profile-error">Erreur : ${data?.error ?? "inconnu"}</p>`;
            return;
        }

        const l = this.lang;
        const { user } = data;
        const header = renderProfileHeader({ avatar: user.avatar, username: user.username || user.email, createdAt: user.created_at }, l);

        const badges = [
            user.is_admin ? '<span class="admin-badge admin-badge--admin">admin</span>' : "",
            user.flagged_suspect ? '<span class="admin-badge admin-badge--suspect">⚠️ suspect</span>' : "",
            user.hidden_from_leaderboard ? '<span class="admin-badge admin-badge--hidden">🙈 masqué</span>' : "",
        ].filter(Boolean).join(" ");

        // `row` (déjà en cache depuis /geoadmin/users) porte les compteurs de session,
        // absents de /geoadmin/users/:id/stats — pas besoin d'un second aller-retour.
        const meta = `
            <div class="admin-profile-meta">
                <p class="admin-profile-email">${escapeHtml(user.email)}${badges ? ` ${badges}` : ""}</p>
                <p class="admin-profile-meta-line">${row?.session_count ?? 0} session(s) · dernière activité ${fmtDateTime(row?.last_active)} · ${row?.failed_login_count ?? 0} échec(s) de connexion</p>
                <p class="admin-profile-meta-line">Sauvegarde v${data.saves.version} · màj ${fmtDateTime(data.saves.updated_at)} · ${data.discoveredCount}/170 pays explorés</p>
            </div>
        `;

        const actions = `
            <div class="admin-profile-actions">
                <button class="admin-btn admin-btn--email" data-id="${id}">📧 Modifier l'e-mail</button>
                <button class="admin-btn admin-btn--edit" data-id="${id}">✏️ Modifier le pseudo</button>
                <button class="admin-btn admin-btn--flag${user.flagged_suspect ? " is-active" : ""}" data-id="${id}">🚩 ${user.flagged_suspect ? "Retirer le flag suspect" : "Marquer comme suspect"}</button>
                <button class="admin-btn admin-btn--hide${user.hidden_from_leaderboard ? " is-active" : ""}" data-id="${id}">${user.hidden_from_leaderboard ? "🙈 Réafficher dans le classement" : "👁️ Masquer du classement"}</button>
                <button class="admin-btn admin-btn--sessions" data-id="${id}">🔒 Révoquer les sessions</button>
                <button class="admin-btn admin-btn--delete" data-id="${id}">🗑️ Supprimer le compte</button>
            </div>
        `;

        const streaks = [
            this.renderStreakCard("Défi quotidien", data.dailyStreak),
            this.renderStreakCard("Défi quotidien Expert", data.dailyHardStreak),
        ].filter(Boolean).join("");
        const streaksHtml = streaks ? `<div class="admin-profile-stats">${streaks}</div>` : "";

        const games = this.buildGamesMap(data.stats);
        const { html: statsHtml, hasStats } = renderProfileStats(games, l, data.dailyMedals);

        this.profileBody.innerHTML = `
            ${header}
            ${meta}
            ${actions}
            ${streaksHtml}
            ${hasStats ? statsHtml : '<p class="player-profile-empty">Aucun score enregistré.</p>'}
        `;

        this.profileBody.querySelector(".admin-btn--email")?.addEventListener("click", () => this.editEmail(id));
        this.profileBody.querySelector(".admin-btn--edit")?.addEventListener("click", () => this.editUser(id));
        this.profileBody.querySelector(".admin-btn--flag")?.addEventListener("click", () => this.toggleSuspect(id));
        this.profileBody.querySelector(".admin-btn--hide")?.addEventListener("click", () => this.toggleHidden(id));
        this.profileBody.querySelector(".admin-btn--sessions")?.addEventListener("click", () => this.revokeSessions(id));
        this.profileBody.querySelector(".admin-btn--delete")?.addEventListener("click", () => this.deleteUser(id));
    }

    async editEmail(id) {
        const user = this.users.find(u => u.id === id);
        if (!user) return;
        const input = prompt(`Nouvel e-mail pour ${user.email} :`, user.email);
        if (input === null) return;
        const email = input.trim().toLowerCase();
        if (!email || email === user.email) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            alert("Adresse e-mail invalide.");
            return;
        }
        const { ok, data } = await api("PATCH", `/geoadmin/users/${id}`, { email });
        if (!ok) {
            alert(`Erreur : ${errorLabel(data?.error)}`);
            return;
        }
        user.email = data.email;
        this.renderTable();
        if (this.selectedId === id) this.showStats(id);
    }

    async editUser(id) {
        const user = this.users.find(u => u.id === id);
        if (!user) return;
        const newUsername = prompt(`Nouveau pseudo pour ${user.email} :`, user.username ?? "");
        if (newUsername === null) return;
        const { ok, data } = await api("PATCH", `/geoadmin/users/${id}`, { username: newUsername.trim() || null });
        if (!ok) {
            alert(`Erreur : ${errorLabel(data?.error)}`);
            return;
        }
        user.username = data.username;
        this.renderTable();
        if (this.selectedId === id) this.showStats(id);
    }

    async toggleSuspect(id) {
        const user = this.users.find(u => u.id === id);
        if (!user) return;
        const { ok, data } = await api("PATCH", `/geoadmin/users/${id}`, { flagged_suspect: !user.flagged_suspect });
        if (!ok) {
            alert(`Erreur : ${errorLabel(data?.error)}`);
            return;
        }
        user.flagged_suspect = data.flagged_suspect;
        this.renderTable();
        if (this.selectedId === id) this.showStats(id);
    }

    async toggleHidden(id) {
        const user = this.users.find(u => u.id === id);
        if (!user) return;
        const { ok, data } = await api("PATCH", `/geoadmin/users/${id}`, { hidden_from_leaderboard: !user.hidden_from_leaderboard });
        if (!ok) {
            alert(`Erreur : ${errorLabel(data?.error)}`);
            return;
        }
        user.hidden_from_leaderboard = data.hidden_from_leaderboard;
        this.renderTable();
        if (this.selectedId === id) this.showStats(id);
    }

    async revokeSessions(id) {
        const user = this.users.find(u => u.id === id);
        if (!user) return;
        if (!confirm(`Révoquer toutes les sessions de ${user.email} ?`)) return;
        const { ok, data } = await api("DELETE", `/geoadmin/users/${id}/sessions`);
        if (!ok) {
            alert(`Erreur : ${data?.error ?? "inconnu"}`);
            return;
        }
        user.session_count = 0;
        this.renderTable();
        if (this.selectedId === id) this.showStats(id);
    }

    async deleteUser(id) {
        const user = this.users.find(u => u.id === id);
        if (!user) return;
        if (!confirm(`Supprimer définitivement le compte ${user.email} ? Cette action est irréversible.`)) return;
        const { ok, data } = await api("DELETE", `/geoadmin/users/${id}`);
        if (!ok) {
            alert(`Erreur : ${data?.error ?? "inconnu"}`);
            return;
        }
        this.users = this.users.filter(u => u.id !== id);
        if (this.selectedId === id) this.closeProfilePopup();
        this.renderTable();
    }
}

const ERROR_LABELS = {
    email_taken: "cet e-mail est déjà utilisé par un autre compte",
    username_taken: "ce pseudo est déjà pris",
    not_found: "compte introuvable",
    forbidden: "accès refusé",
};

function errorLabel(code) {
    return ERROR_LABELS[code] ?? code ?? "inconnu";
}

function escHtml(str) {
    return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}
